import { Link } from 'react-router-dom';
import {
  ArrowRight,
  AtSign,
  BookOpen,
  Boxes,
  Briefcase,
  CalendarCheck,
  ClipboardList,
  Crosshair,
  FolderCheck,
  GraduationCap,
  Lightbulb,
  Monitor,
  Network,
  Presentation,
  Rocket,
  Palette,
  Share2,
  Shapes,
  Signpost,
  TrendingUp,
  UserSearch,
  Users,
} from 'lucide-react';
import { Reveal } from '../Reveal';
import { cn } from '@/lib/utils';

const SERVICE_ICONS = {
  'folder-check': FolderCheck,
  shapes: Shapes,
  users: Users,
  'graduation-cap': GraduationCap,
  'user-search': UserSearch,
  briefcase: Briefcase,
  'calendar-check': CalendarCheck,
  network: Network,
  'share-2': Share2,
  'at-sign': AtSign,
  lightbulb: Lightbulb,
  monitor: Monitor,
  crosshair: Crosshair,
  presentation: Presentation,
  signpost: Signpost,
  'book-open': BookOpen,
  'clipboard-list': ClipboardList,
  boxes: Boxes,
  rocket: Rocket,
  'trending-up': TrendingUp,
  palette: Palette,
};

const ServicePackageCard = ({ service, index, accent, accentDark, accentSoft, discussHref, testidPrefix, balanced, topAccentBar, discussLabel }) => {
  const Icon = SERVICE_ICONS[service.icon];
  const isDiscuss = service.cta === 'discuss';
  const href = isDiscuss ? discussHref : service.href;

  return (
    <Reveal
      delay={(index % 4) * 0.08}
      className={cn('h-full', balanced && 'w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]')}
    >
      <article
        data-testid={`${testidPrefix}-service-card-${service.slug}`}
        data-service-slug={service.slug}
        data-purchase-mode={service.purchaseMode}
        data-purchasable={service.purchasable}
        data-availability={service.availability}
        className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-7 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-[color:var(--accent-hover)] hover:shadow-lift"
        style={{
          '--accent-hover': `${accent}66`,
          ...(topAccentBar ? { borderTopWidth: '3px', borderTopColor: accent } : {}),
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <span
            className="flex h-12 w-12 items-center justify-center rounded-lg"
            style={{ backgroundColor: accentSoft, color: accent }}
          >
            <Icon className="h-6 w-6" aria-hidden="true" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: accentDark }}>
            {service.category}
          </span>
        </div>
        <h3 className="mt-5 text-lg font-extrabold tracking-tight text-navy-900">{service.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{service.shortDescription}</p>
        <Link
          to={href}
          data-testid={`${testidPrefix}-service-cta-${service.slug}`}
          className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-bold transition-colors"
          style={{ color: accent }}
        >
          {isDiscuss ? discussLabel : 'View Details'}
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      </article>
    </Reveal>
  );
};

/**
 * Purchase-ready service/package grid shared by service-line pages.
 * layout 'grid4'  — 4-column grid (People)
 * layout 'balanced3' — 3-per-row flex wrap, final row centred (Growth 3+2)
 */
export const ServicePackages = ({
  services,
  section,
  accent,
  accentDark,
  accentSoft,
  discussHref,
  id,
  testidPrefix,
  layout = 'grid4',
  topAccentBar = false,
  index = '03',
  discussLabel = 'Discuss Your Needs',
}) => {
  const sorted = [...services].sort((a, b) => a.displayOrder - b.displayOrder);
  const balanced = layout === 'balanced3';

  return (
    <section
      id={id}
      aria-labelledby={`${testidPrefix}-services-heading`}
      data-testid={`${testidPrefix}-services-section`}
      className="bg-mist py-20 sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center" data-testid={`${testidPrefix}-services-header`}>
          <p className="flex items-center justify-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest" style={{ color: accent }}>{index}</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{section.eyebrow}</span>
          </p>
          <h2 id={`${testidPrefix}-services-heading`} className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            {section.heading}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{section.intro}</p>
        </div>

        <div
          className={cn(
            'mt-14',
            balanced ? 'flex flex-wrap justify-center gap-6' : 'grid gap-6 sm:grid-cols-2 lg:grid-cols-4'
          )}
        >
          {sorted.map((service, i) => (
            <ServicePackageCard
              key={service.slug}
              service={service}
              index={i}
              accent={accent}
              accentDark={accentDark}
              accentSoft={accentSoft}
              discussHref={discussHref}
              testidPrefix={testidPrefix}
              balanced={balanced}
              topAccentBar={topAccentBar}
              discussLabel={discussLabel}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
